import { ReactNode, useEffect, useState } from 'react';
import css from './ScheduleAppointmentWizard.module.css';
import {
  ProgressStep,
  ProgressIndicator,
  ProgressIndicatorType,
} from '../../core';
import {
  AppointmentInfoStep,
  ContactInfoStep,
  DateTimeStep,
  LocationStep,
  PatientStep,
  ReviewAndSubmitStep,
} from './steps';
import { ResponsiveSizeUtility } from '../../utils';
import clsx from 'clsx';
import { useSchedulingContext } from '../context';
import { HaveMRN, PreviouslySeen } from '../../api/models';
import { useScreenSize } from '@mchs/web-core';
import { GenericError } from '../genericError';

interface StepDefinition {
  name: string;
  view: ReactNode;
  overrideNextStep?: string;
}

interface ScheduleAppointmentWizardProps {
  onSubmitAppointment: () => void;
  errorText?: string;
  onWizardProgressed: () => void;
}

const ScheduleAppointmentWizard: React.FC<ScheduleAppointmentWizardProps> = (
  props: ScheduleAppointmentWizardProps
) => {
  const schedulingDetails = useSchedulingContext();
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const screenSize = useScreenSize();

  useEffect(() => {
    if (props.errorText !== undefined) {
      setActiveStepIndex(3); //date & time screen
    }
  }, [props.errorText]);

  const onCompletedStepClicked = (stepIndex: number) => {
    props.onWizardProgressed();
    setActiveStepIndex(stepIndex);
  };

  const onContinue = () => {
    props.onWizardProgressed();
    setActiveStepIndex(activeStepIndex + 1);
  };

  const onSubmit = () => {
    props.onWizardProgressed();
    props.onSubmitAppointment();
  };

  const contactInfoStepName = 'Contact Info';
  const allSteps: StepDefinition[] = [
    {
      name: 'Patient',
      view: <PatientStep onContinue={onContinue} />,
    },
    {
      name: 'Appointment Info',
      view: <AppointmentInfoStep onContinue={onContinue} />,
    },
    {
      name: 'Location',
      view: <LocationStep onContinue={onContinue} />,
    },
    {
      name: 'Date & Time',
      view: <DateTimeStep onContinue={onContinue} />,
    },
    {
      name: contactInfoStepName,
      view: <ContactInfoStep onContinue={onContinue} />,
    },
    {
      name: 'Review & Submit',
      view: <ReviewAndSubmitStep onSubmit={onSubmit} />,
      overrideNextStep: 'Confirmation',
    },
  ];
  const steps =
    schedulingDetails.patientDetails?.previouslySeen === PreviouslySeen.Yes &&
    schedulingDetails.patientDetails?.haveMRN === HaveMRN.Yes
      ? allSteps.filter((step) => step.name !== contactInfoStepName)
      : allSteps;

  const narrowWidth = ResponsiveSizeUtility.isNarrowWidth(screenSize.width);

  return (
    <div className={css.container}>
      {activeStepIndex < steps.length ? (
        <>
          <div
            className={clsx(
              css.progressIndicatorContainer,
              narrowWidth ? css.progressLeft : null
            )}
          >
            <ProgressIndicator
              indicatorType={
                narrowWidth
                  ? ProgressIndicatorType.Circle
                  : ProgressIndicatorType.HorizontalSteps
              }
              steps={steps.map<ProgressStep>((step) => {
                return {
                  name: step.name,
                  overrideNextStep: step.overrideNextStep,
                };
              })}
              activeStepIndex={activeStepIndex}
              onCompletedStepClicked={onCompletedStepClicked}
            />
          </div>
          {props.errorText !== undefined && (
            <GenericError errorCode={props.errorText} />
          )}
          <div
            className={clsx(
              css.content,
              narrowWidth ? css.contentNarrow : null
            )}
          >
            {steps[activeStepIndex].view}
          </div>
        </>
      ) : (
        <div>Done</div>
      )}
    </div>
  );
};

export default ScheduleAppointmentWizard;
